import * as React from 'react';
import { Paper, PromptConfirmation } from 'ui';

import { withPrompt, PromptProps, SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import { routeConstants } from 'app/routes/route-constants';
import * as navigate from 'app/area/shared/navigation';

import { getDashboardAdminAccess } from '../common/get-dashboard-admin-access';
import { getBackRoute } from '../common/route';
import { dashboardAdminTranslation } from '../constants';
import { DashboardAdminState } from '../types-store';

import { DashboardFormSections } from './components/dashboard-form-sections';
import { actionCreators } from './actions';
import { DashboardFormSelectorProps, selectors } from './selectors';
import { DashboardItem } from './types-store';
import { scrollToTop } from 'app/infrastructure/platform/routing';

type DashboardFormProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & DashboardAdminState
  & DashboardFormSelectorProps
  & typeof actionCreators;

interface DashboardFormState {
  showTranslations: boolean;
}

class DashboardFormComponent extends React.PureComponent<DashboardFormProps, DashboardFormState> {
  public static statePath: string = STATE.dashboardAdministration;

  public state: Readonly<DashboardFormState> = {
    showTranslations: true
  };

  public componentDidMount() {
    scrollToTop();
    this.props.loadLanguageOptions();

    if (hasTranslationsLoaded(this.props.translation)) {
      this.initForm();
    }
  }

  public componentDidUpdate(prevProps: DashboardFormProps) {
    if (this.props.id !== prevProps.id ||
      translationChanged(this.props.translation, prevProps.translation)) {
      this.initForm();
    }

    if (prevProps?.form?.validation?.language !== this.props?.form?.validation?.language) {
      this.updateTranslationsToggle();
    }
  }

  public componentWillUnmount() {
    this.props.clearForm();
  }

  private initForm = () => {
    const { id, favouriteId, sharedState,
      createDashboard, loadDashboard, displayError, translation } = this.props;

    const dashboardAccess = getDashboardAdminAccess(sharedState);
    if (!dashboardAccess) {
      displayError(translation.no_access_to_dashboard_admin);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(id)) {
      loadDashboard(id, favouriteId, translation.must_be_filled_out);
    } else {
      createDashboard();
    }
  };

  private switchTranslationsToggle = (showTranslations: boolean) => this.setState({ showTranslations });
  private updateTranslationsToggle = () => {
    if (!!this.props?.form?.validation?.language) {
      this.switchTranslationsToggle(true);
    }
  };

  private saveDashboard = (dashboard: DashboardItem) => {
    const { form: { savedReports, filters } = {}, favouriteId, saveDashboard, translation } = this.props;

    const successMessage = isDefinedIncludingZero(dashboard?.id)
      ? translation.dashboard_updated
      : translation.dashboard_created;

    saveDashboard(dashboard, savedReports, filters, favouriteId, successMessage, translation.fields_are_filled_out_incorrectly);
  };

  private deleteDashboard = () => {
    const { id, favouriteId, deleteDashboard, translation } = this.props;

    deleteDashboard(id, favouriteId, translation.dashboard_deleted);
  };

  private toSavedSearches = () => {
    const { form: { newDashboardId } } = this.props;

    navigate.to({ pathname: routeConstants.dashboardAdminSavedReportsFor(newDashboardId) }, { state: { internal: true } });
  };

  private toList = () => {
    navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
  };

  public render() {

    const { favouriteId, section, sharedState, form, translation,
      options, loadOptions, shouldPrompt,
      updateItem, changeLanguage, copyLanguageToAll, clearEvaluationsValidation, loadEvaluations,
      addFilters, updateFilter, deleteFilter, deleteAllFilters, updateFiltersValidation,
      selectCurrentSavedReports, updateCurrentSavedReports, updateCurrentValidation, loadSavedReportEvaluation,
      addSavedReports, updateSavedReport, deleteSavedReport, deleteAllSavedReports,
      updateValidation, displayError, switchDisplayDisabled
    } = this.props;
    const mainLanguage = sharedState?.language?.value ?? undefined;
    const locale = sharedState?.locale || '';

    return <div className="entity-view">
      <Paper componentId="code-base-paper" className={undefined}>
        {!!this.props.shouldDisplayModal &&
          <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel} />}
        {isDefinedIncludingZero(form.newDashboardId) &&
          <PromptConfirmation
            title={translation.new_dashboard}
            description={translation.connect_to_new_confirmation}
            continueText={translation.connect_to_saved_reports}
            cancelText={translation.later}
            onContinue={this.toSavedSearches}
            onCancel={this.toList} />}
        {!form.languageOptions || !form.dashboardItem || form.fetching
          ? <SpinLoader />
          : <DashboardFormSections
            formState={form}
            favouriteId={favouriteId}
            mainLanguage={mainLanguage}
            saveDashboard={this.saveDashboard}
            deleteDashboard={this.deleteDashboard}
            updateItem={updateItem}
            changeLanguage={changeLanguage}
            copyLanguageToAll={copyLanguageToAll}
            options={options}
            loadOptions={loadOptions}
            addFilters={addFilters}
            updateFilter={updateFilter}
            deleteFilter={deleteFilter}
            deleteAllFilters={deleteAllFilters}
            updateFiltersValidation={updateFiltersValidation}
            selectCurrentSavedReports={selectCurrentSavedReports}
            updateCurrentSavedReports={updateCurrentSavedReports}
            updateCurrentValidation={updateCurrentValidation}
            loadSavedReportEvaluation={loadSavedReportEvaluation}
            addSavedReports={addSavedReports}
            updateSavedReport={updateSavedReport}
            deleteSavedReport={deleteSavedReport}
            deleteAllSavedReports={deleteAllSavedReports}
            loadEvaluations={loadEvaluations}
            clearEvaluationsValidation={clearEvaluationsValidation}
            updateValidation={updateValidation}
            displayError={displayError}
            switchDisplayDisabled={switchDisplayDisabled}
            showTranslations={this.state.showTranslations}
            switchTranslationsToggle={this.switchTranslationsToggle}
            shouldPrompt={shouldPrompt}
            section={section}
            locale={locale}
            translation={translation} />}
      </Paper>
    </div>;
  }
}

const container = containerFactory(
  withPrompt(withOptions(DashboardFormComponent)),
  actionCreators, selectors, dashboardAdminTranslation
);

export { container as DashboardForm };
